import { useEffect, useRef } from 'react';
import { usePurchaseOrder } from '@/hooks/use-purchase-orders';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { isZeroQty, ZERO_QTY_ROW_CLASS } from './zero-qty';
import { ZeroQtyTag } from './zero-qty-tag';

interface Props {
  orderId: string;
}

export function PurchaseOrderPrintPage({ orderId }: Props) {
  const { data, isLoading } = usePurchaseOrder(orderId);
  const order = data?.data;
  // Role-based cost visibility (kept intact — same hook other surfaces use).
  const canSeeCost = useCanSeeCost();
  // Product decision: hide cost/total in the print view to match the PO
  // email (which only shows product/qty/unit). Flip to true to bring them
  // back; the role check still applies.
  const showCostColumns = false;

  // The PO list opens this page in a named tab (po-print-<id>) so a second
  // click reuses the same tab rather than spawning new ones. That reuse
  // re-mounts this component, which would re-fire window.print() — guard with
  // a ref so the OS print dialog only fires once per page load.
  const hasPrintedRef = useRef(false);
  useEffect(() => {
    if (order && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [order]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!order) {
    return <div className="flex h-screen items-center justify-center">Purchase order not found.</div>;
  }

  return (
    <div className="mx-auto max-w-3xl p-8 print:p-4">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Purchase Order #{order.orderNumber}</h1>
          <p className="text-sm text-muted-foreground">Created: {formatDateTime(order.createdAt)}</p>
        </div>
        <div className="text-right">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-sm font-medium capitalize ${
              order.status === 'received' || order.status === 'completed'
                ? 'bg-green-100 text-green-800'
                : order.status === 'cancelled'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-blue-100 text-blue-800'
            }`}
          >
            {order.status}
          </span>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-6 rounded-lg border border-border p-4">
        <div>
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Vendor</h3>
          <p className="mt-1 font-medium">{order.payeeName}</p>
        </div>
        <div>
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Location</h3>
          <p className="mt-1 font-medium">{order.locationName}</p>
        </div>
        {order.payeeInvoiceNo && (
          <div>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">Invoice #</h3>
            <p className="mt-1">{order.payeeInvoiceNo}</p>
          </div>
        )}
        {order.createdByName && (
          <div>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">Created By</h3>
            <p className="mt-1">{order.createdByName}</p>
          </div>
        )}
        {order.paymentDueDate && (
          <div>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">Payment Due</h3>
            <p className="mt-1">{order.paymentDueDate}</p>
          </div>
        )}
        {order.receivedDate && (
          <div>
            <h3 className="text-xs font-semibold uppercase text-muted-foreground">Received Date</h3>
            <p className="mt-1">{order.receivedDate}</p>
          </div>
        )}
      </div>

      {order.notes && (
        <div className="mb-6 rounded-lg border border-border p-4">
          <h3 className="text-xs font-semibold uppercase text-muted-foreground">Notes</h3>
          <p className="mt-1 text-sm whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Product</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Unit</th>
            <th className="py-2 text-right text-xs font-semibold uppercase text-muted-foreground">Qty</th>
            {showCostColumns && canSeeCost && (
              <>
                <th className="py-2 text-right text-xs font-semibold uppercase text-muted-foreground print:hidden">
                  Cost/Unit
                </th>
                <th className="py-2 text-right text-xs font-semibold uppercase text-muted-foreground">Line Total</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {order.items?.map((item: any) => {
            const lineTotal = Number(item.quantity) * Number(item.costPerUnit);
            return (
              <tr
                key={item.id}
                className={`border-b border-border ${isZeroQty(item.quantity) ? ZERO_QTY_ROW_CLASS : ''}`}
              >
                <td className="py-2">
                  <div className="font-medium">
                    {item.productName}
                    {isZeroQty(item.quantity) && <ZeroQtyTag />}
                  </div>
                  {item.productBarcode && <div className="text-xs text-muted-foreground">{item.productBarcode}</div>}
                </td>
                <td className="py-2 text-sm">{item.unitOfMeasure}</td>
                <td className="py-2 text-right text-sm">{Number(item.quantity)}</td>
                {showCostColumns && canSeeCost && (
                  <>
                    <td className="py-2 text-right text-sm print:hidden">{formatCurrency(item.costPerUnit)}</td>
                    <td className="py-2 text-right text-sm font-medium">{formatCurrency(lineTotal)}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
        {showCostColumns && canSeeCost && (
          <tfoot>
            <tr className="border-t-2 border-border">
              <td colSpan={4} className="py-3 text-right font-semibold">
                Total
              </td>
              <td className="py-3 text-right text-lg font-bold">{formatCurrency(order.totalCost)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      <div className="mt-8 print:hidden">
        <button
          onClick={() => window.print()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Print
        </button>
        <button
          onClick={() => window.history.back()}
          className="ml-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back
        </button>
      </div>
    </div>
  );
}
